import React, { useEffect, useRef, useState } from "react";
import { TextField, mergeStyles } from '@fluentui/react';
import styles from '../../../WorkSpaceItemList.module.css';
import CheckIcon from "@mui/icons-material/Check";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { CircularProgress, Tooltip, Typography } from '@mui/material';
import EditIcon from "@mui/icons-material/Edit";
import { useTranslation } from 'react-i18next';
import Loader from "react-ts-loaders";
import { SetDescription } from "../../../../../services/Item/ItemServicesCRUD";
import { description } from "../../../WorkSpaceItemListStyle/ColumnStandard";
import descriptionStyles from './DescriptionFieldStyle.module.css';

interface IDescriptionField {
    indexOfArray: number;
    itemType?: string;
    refRef?: React.MutableRefObject<any>;
    descriptionFile?: string;
    id?: string;
    name?: string;
    isDetailPage?: boolean;
    layoutPendencies?: boolean;
    setPopupError: (set: boolean, message: string) => void;
    setPopupOk: (set: boolean, message: string) => void;
    updateNameDescriptionByIndex: (index: number, name: string, isUpload: boolean, description: string) => void;
    isUpload: boolean;
    boxLayout: boolean;
    setIsEditingItemDesc?: (set: boolean) => void;
}

const loaderDesc = mergeStyles({
    justifyContent: "right",
    paddingTop: "10px",
    float: "right",
});

export function DescriptionField({
  indexOfArray,
  itemType,
  refRef,
  descriptionFile,
  id,
  name,
  isDetailPage,
  layoutPendencies,
  setPopupError,
  setPopupOk,
  updateNameDescriptionByIndex,
  isUpload,
  boxLayout,
  setIsEditingItemDesc
}: IDescriptionField) {

  const [descTimed, setDescTimed] = useState<string | undefined>(descriptionFile);
  const [editDesc, setEditDesc] = useState<boolean>(false);
  const [isShown, setIsShown] = useState(false);
  const [isWaitingDescription, setIsWaitingDescription] = useState<boolean>(false);
  const [isTextTruncated, setIsTextTruncated] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();

  const checkTruncation = () => {
    if (textRef.current) {
      setIsTextTruncated(textRef.current.scrollWidth > textRef.current.clientWidth);
    }
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleSetDescription();
    } else if (event.key === 'Escape') {
      onClickCancelEditDesc();
    }
  };

  function onClickCancelEditDesc() {
    setEditDesc(false);
    setIsShown(false);
  }

  function handleSetDescription() {
    setEditDesc(false);
    if (id != undefined && name != undefined) {
      setIsWaitingDescription(true);
      SetDescription({
        itemDescription: descTimed != undefined ? descTimed : "",
        itemId: id,
      })
        .then(() => {
          updateNameDescriptionByIndex(indexOfArray, name, isUpload, descTimed != undefined ? descTimed : "");
          setPopupOk(true, "Modificado!");
        })
        .catch((error) => {
          if (error.response?.data && typeof error.response.data === "string") {
            setPopupError(true, error.response.data);
          } else {
            setPopupError(true, t("renameItem.error"));
          }
        })
        .finally(() => {
          setIsWaitingDescription(false);
        });
    } else {
      setPopupError(true, "Erro");
    }
  }

  function onClickEditDesc() {
    setDescTimed(descriptionFile == undefined ? "" : descriptionFile);
    setEditDesc(true);
  }

  const onChangeDescription = React.useCallback(
    (
      event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
      newValue?: string
    ) => {
      if (newValue != undefined) {
        setDescTimed(newValue);
      }
    },
    []
  );

  useEffect(() => {
    if (setIsEditingItemDesc) {
      setIsEditingItemDesc(editDesc);
    }
  }, [editDesc]);

  if (editDesc && !layoutPendencies) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gridArea: description,
          width: '100%',
          minWidth: 0,
        }}
        ref={refRef}
      >
        <TextField
          maxLength={500}
          value={descTimed}
          onChange={onChangeDescription}
          onKeyDown={onKeyDown}
          styles={{
            root: { flexGrow: 1, minWidth: 0 },
            fieldGroup: {
              height: '30px',
              minHeight: '30px',
              borderRadius: '8px',
            },
            field: {
              whiteSpace: 'nowrap',
              borderRadius: '8px',
            },
          }}
          borderless
          autoFocus
        />
        <div style={{ display: 'flex', flexShrink: 0 }}>
          <CheckIcon onClick={handleSetDescription} sx={{ cursor: 'pointer' }} />
          <CloseOutlinedIcon onClick={onClickCancelEditDesc} sx={{ cursor: 'pointer' }} />
        </div>
      </div>
    );
  }

  const hasDescription = descriptionFile != undefined && descriptionFile !== "";

  const textContent = (
    <div
      ref={textRef}
      onMouseEnter={checkTruncation}
      style={{
        display: 'block',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        width: '100%',
        minWidth: 0,
      }}
    >
      {hasDescription ? descriptionFile : (
        <span className={`${styles.emptySlot}`}>{t("WithoutRule.withoutDesc")}</span>
      )}
    </div>
  );

  return (
    <div
      ref={refRef}
      style={{
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        gridArea: description,
        minWidth: 0,
        flex: isDetailPage ? 1 : undefined,
        width: isDetailPage ? undefined : '100%',
        minHeight: isDetailPage ? '32px' : undefined,
      }}
      onMouseEnter={itemType === "folder" ? undefined : () => setIsShown(true)}
      onMouseLeave={itemType === "folder" ? undefined : () => setIsShown(false)}
    >
      <Tooltip
        title={isTextTruncated && hasDescription ? descriptionFile : ""}
        disableInteractive
        enterDelay={300}
        placement="bottom-start"
      >
        {isDetailPage ? (
          <Typography
            component="div"
            sx={{
              textAlign: 'left',
              font: 'normal normal normal 16px/24px Segoe UI',
              letterSpacing: '0px',
              color: '#2D2D2D',
              opacity: 1,
              flex: 1,
              minWidth: 0,
            }}
          >
            {textContent}
          </Typography>
        ) : (
          <div style={{ minWidth: 0, width: '100%', flexShrink: 1 }}>
            {textContent}
          </div>
        )}
      </Tooltip>

      {isWaitingDescription && (
        <>
          <Loader size={20} className={loaderDesc} />
          <CircularProgress color="inherit" size="20px" style={{ margin: "4px", flexShrink: 0 }} />
        </>
      )}

      {isShown && !layoutPendencies && !isWaitingDescription && (
        <div
          className={isDetailPage ? undefined : descriptionStyles.editIconStyle}
          style={{ flexShrink: 0, marginLeft: '4px' }}
        >
          <EditIcon
            className={styles.EditiconName}
            onClick={onClickEditDesc}
            style={{ color: "rgb(17, 77, 136)", cursor: "pointer" }}
          />
        </div>
      )}
    </div>
  );
}